import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/* Videos and the manifest live in public/media/testimonials, so adding a
   customer means dropping in a file and one entry in testimonials.json. */
const BASE = '/media/testimonials';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700&family=Montserrat:wght@500;600;700;800&display=swap');
.ts{--ink:#0F241D;--dim:#68776F;--line:#E2E9E5;--gold:#C9922A;--green:#0D3B2E;--paper:#fff;
  font-family:Montserrat,system-ui,sans-serif;color:var(--ink);padding-bottom:56px}
.ts-eb{font-size:10.5px;font-weight:800;letter-spacing:.2em;text-transform:uppercase;color:var(--gold);margin-bottom:7px}
.ts-h{font-family:Fraunces,Georgia,serif;font-size:clamp(25px,3.3vw,33px);font-weight:700;margin:0;letter-spacing:-.02em;line-height:1.06}
.ts-l{color:var(--dim);font-size:13.5px;margin-top:8px;max-width:58ch;line-height:1.55}
.ts-count{font-size:12.5px;color:var(--dim);font-weight:600;margin:18px 0 0}
.ts-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(290px,1fr));gap:22px;margin-top:20px}
.ts-card{display:flex;flex-direction:column;border:1px solid var(--line);padding:0;font:inherit;text-align:left;
  cursor:pointer;border-radius:18px;overflow:hidden;background:var(--paper);box-shadow:0 1px 2px rgba(15,36,29,.05);
  transition:transform .28s cubic-bezier(.2,.8,.3,1),box-shadow .28s,border-color .28s}
.ts-card:hover{transform:translateY(-4px);border-color:#CFDBD5;box-shadow:0 20px 38px -22px rgba(15,36,29,.45)}
.ts-card:focus-visible{outline:3px solid var(--gold);outline-offset:3px}
.ts-thumb{position:relative;aspect-ratio:4/3;overflow:hidden;background:#132A22;flex:0 0 auto}
.ts-thumb img{width:100%;height:100%;object-fit:cover;display:block;transition:transform .55s cubic-bezier(.2,.8,.3,1)}
.ts-card:hover .ts-thumb img{transform:scale(1.045)}
.ts-play{position:absolute;inset:0;display:grid;place-items:center;background:rgba(10,26,20,.26);transition:background .24s}
.ts-card:hover .ts-play{background:rgba(10,26,20,.12)}
.ts-play span{width:56px;height:56px;border-radius:50%;background:rgba(255,255,255,.95);color:var(--green);
  display:grid;place-items:center;font-size:19px;padding-left:3px;box-shadow:0 4px 16px rgba(0,0,0,.35);
  transition:transform .24s cubic-bezier(.3,1.4,.5,1)}
.ts-card:hover .ts-play span{transform:scale(1.1)}
.ts-len{position:absolute;bottom:10px;right:11px;background:rgba(10,26,20,.82);color:#fff;font-size:11px;
  font-weight:700;padding:3px 9px;border-radius:6px}
.ts-cb{padding:16px 17px 17px;display:flex;flex-direction:column;flex:1 1 auto}
.ts-cb h2{font-family:Fraunces,Georgia,serif;font-size:18.5px;margin:0;font-weight:700;letter-spacing:-.013em;line-height:1.24}
.ts-city{font-size:12.5px;color:var(--gold);font-weight:700;margin-top:4px}
.ts-work{font-size:13px;color:var(--dim);margin-top:7px;line-height:1.5}
.ts-meta{margin-top:auto;padding-top:12px;border-top:1px solid #EFF3F1;display:flex;align-items:center;gap:8px}
.ts-watch{display:inline-flex;align-items:center;gap:8px;background:var(--green);color:#fff;border-radius:999px;
  padding:10px 19px;font-size:13.5px;font-weight:800;box-shadow:0 2px 8px -3px rgba(13,59,46,.5)}
.ts-card:hover .ts-watch{filter:brightness(1.08)}
.ts-player{position:fixed;inset:0;background:rgba(6,15,12,.95);z-index:9999;display:flex;align-items:center;
  justify-content:center;padding:22px;flex-direction:column;gap:14px}
.ts-player video{max-width:min(1000px,94vw);max-height:78vh;border-radius:10px;background:#000}
.ts-ptitle{color:#fff;font-size:15px;font-weight:700;text-align:center}
.ts-ptitle span{display:block;font-size:12.5px;font-weight:600;color:rgba(255,255,255,.7);margin-top:4px}
.ts-pclose{position:absolute;top:18px;right:20px;width:44px;height:44px;border-radius:50%;border:none;
  background:rgba(255,255,255,.16);color:#fff;font-size:18px;cursor:pointer}
.ts-pclose:hover{background:rgba(255,255,255,.3)}
.ts-empty{border:1px dashed #D5DEDA;border-radius:16px;padding:34px;text-align:center;color:var(--dim);font-size:13.5px}
@media(max-width:600px){.ts-watch{width:100%;justify-content:center;padding:13px 19px;font-size:14.5px}
  .ts-meta{display:block}}
`;

export default function Testimonials() {
  const [params, setParams] = useSearchParams();
  const slug = params.get('v');
  const [list, setList] = useState(null);

  useEffect(() => {
    fetch(`${BASE}/testimonials.json`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => setList(Array.isArray(d) ? d : []))
      .catch(() => setList([]));
  }, []);

  const playing = list?.find(t => t.slug === slug) || null;
  const open = (s) => {
    const n = new URLSearchParams(params);
    s ? n.set('v', s) : n.delete('v');
    setParams(n);
  };

  useEffect(() => {
    if (!playing) return;
    const esc = (e) => { if (e.key === 'Escape') open(null); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [playing]);

  /* The thumbnail sits alongside the video with the same base name. */
  const thumbOf = (t) => `${BASE}/${t.file.replace(/\.[^.]+$/, '')}.jpg`;

  return (
    <>
      <style>{CSS}</style>
      <div className="page-header"><h2>💬 What Customers Say</h2></div>

      <div className="page-body ts">
        {playing && (
          <div className="ts-player" onClick={e => { if (e.target === e.currentTarget) open(null); }}>
            <video src={`${BASE}/${playing.file}`} controls autoPlay playsInline />
            <div className="ts-ptitle">
              {playing.name}
              <span>{[playing.city, playing.work].filter(Boolean).join(' · ')}</span>
            </div>
            <button className="ts-pclose" aria-label="Close" onClick={() => open(null)}>✕</button>
          </div>
        )}

        <div className="ts-eb">CPR Home Solutions</div>
        <h1 className="ts-h">What customers say</h1>
        <div className="ts-l">
          Real customers, in their own words. Nothing convinces a homeowner like
          hearing it from someone who already said yes.
        </div>

        {!list ? <div style={{ padding: 26, color: '#aaa' }}>Loading…</div>
          : list.length === 0 ? <div className="ts-empty" style={{ marginTop: 20 }}>No testimonials yet.</div>
          : (<>
            <div className="ts-count">{list.length} testimonial{list.length === 1 ? '' : 's'}</div>
            <div className="ts-grid">
              {list.map(t => (
                <button key={t.slug} className="ts-card" onClick={() => open(t.slug)}>
                  <span className="ts-thumb">
                    <img src={thumbOf(t)} alt={t.name} loading="lazy"
                      onError={e => { e.currentTarget.style.display = 'none'; }} />
                    <span className="ts-play"><span aria-hidden>▶</span></span>
                    {t.length && <span className="ts-len">{t.length}</span>}
                  </span>
                  <span className="ts-cb">
                    <h2>{t.name}</h2>
                    {t.city && <span className="ts-city">{t.city}</span>}
                    {t.work && <span className="ts-work">{t.work}</span>}
                    <span className="ts-meta">
                      <span className="ts-watch">Watch <span aria-hidden>→</span></span>
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </>)}
      </div>
    </>
  );
}
